import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../../common/reducers/utilities";
import { ensureArray, groupBy } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = note => note.internalid;

const equipmentNotesSlice = createSlice({
    name: 'equipmentNotes',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(equipmentid => delete state.data[equipmentid]);
            const toSet = ensureArray(action.payload.data);
            const byEquipment = groupBy(toSet, 'equipmentid');
            Object.entries(byEquipment).forEach(([ equipmentid, notes ]) => {
                state.data[equipmentid] = notes;
            });
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byEquipment = groupBy(toUpdate, 'equipmentid');
            Object.entries(byEquipment).forEach(([ equipmentid, notes ]) => {
                const ids = new Set(notes.map(n => key(n)));
                state.data[equipmentid] = (state.data[equipmentid]?.filter(n => !ids.has(key(n))) ?? []).concat(notes);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byEquipment = groupBy(toRemove, 'equipmentid');
            Object.entries(byEquipment).forEach(([equipmentid, notes]) => {
                if (state.data[equipmentid]) {
                    const toRemove = new Set(notes.map(n => n.id));
                    state.data[equipmentid] = state.data[equipmentid].filter(n => !toRemove.has(n.id));
                    if (state.data[equipmentid].length === 0) {
                        delete state.data[equipmentid];
                    }
                }
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => equipmentNotesSlice.getInitialState());
    }
});


// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'equipmentNotes', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'equipmentNotes');


const fetchEquipmentNotes = equipmentid => network => network.get('/api/v1/equipmentnotes/' + encodeURIComponent(equipmentid), data => equipmentNotesSlice.actions.set({ data, ids: [equipmentid] }), visualizeFailure('get'));
export const addEquipmentNote = (network, data) => network.post('/api/v1/equipmentnote', data, equipmentNotesSlice.actions.addOrUpdate, visualizeFailure('create'));
// #endregion

// #region selectors
const selectAllEquipmentNoteData = state => state.equipmentNotes.data;
const selectEquipmentNotes = equipmentid => createSelector([selectAllEquipmentNoteData], notes => notes[equipmentid] ?? []);
// #endregion

const listen = {
    equipmentNoteCreated: (note, { dispatch, websocket }) => {
        markFetched(websocket, 'equipment_note', key(note))
        dispatch(equipmentNotesSlice.actions.addOrUpdate(note))
    }
}

const equipmentInitNotesByID = initMapFactory(fetchEquipmentNotes, listen);
registerEntityBasedFetch('equipment', equipmentInitNotesByID);

const cleanup = { root: equipmentNotesSlice.name, all: state => Object.values(selectAllEquipmentNoteData(state)).flat(), removeAll: equipmentNotesSlice.actions.remove, key };
export const [useEquipmentNotes] = dataHookFactory(selectEquipmentNotes, equipmentInitNotesByID, { cleanup });

const reducer = { name: equipmentNotesSlice.name, reducer: equipmentNotesSlice.reducer }
export default reducer;